import axios from 'axios';
import Signup from '@/models/signup'
import User from '@/models/user'
import Folder from '@/models/folder'
import Message from '@/models/message'

function defaultRequestConfig(token) {
	return {
		'baseURL': process.env.VUE_APP_API_URL,
		'headers': {
			'Content-Type': 'application/vnd.api+json',
			'Authorization': `Bearer ${token}`,
			'Accept': 'application/vnd.api+json, application/json, text/plain, */*'
		}
	};
}

export default {

	/* *********************************************************************
	 * Non-Authenticated Routines
	 * ********************************************************************* */

	signup (signup) {
		return axios.post(`/signup`, signup.toJSONapi(), {'baseURL': process.env.VUE_APP_API_URL} );
	},
	confirmation (token) {
		return axios.get(`/confirmation/${token}`, {'baseURL': process.env.VUE_APP_API_URL} );
	},
	forgotAccount (email) {
		email = email.toLowerCase().trim();
		return axios.post(`/password`, {"user":{"email":email}}, {'baseURL': process.env.VUE_APP_API_URL} );
	},
	changePassword (token, data) {
		if (data.token && data.token != '') return axios.put(`/password`, {"user": {"reset_password_token": data.token, "password": data.password}}, {'baseURL': process.env.VUE_APP_API_URL} );
		else if (data.old) return axios.put(`/signup`, {"user": {"current_password": data.old, "password": data.new, "password_confirmation": data.confirmation, "email": data.email}}, {'baseURL': process.env.VUE_APP_API_URL, 'headers': {'Authorization': `Bearer ${token}`}} );
	},

	/* *********************************************************************
	 * Authentication Routines
	 * ********************************************************************* */
	login (email, password, locale) {
		return axios.post(`${process.env.VUE_APP_API_URL}/login`, {
			'user': { 'login': email, 'password': password, 'locale': locale }
		});
	},
	logout (token) {
		return axios.delete(`/login`, defaultRequestConfig(token));
	},
	refreshToken (token) {
		return axios.get(`/token/refresh`, defaultRequestConfig(token));
	},
	getUserProfile (token) {
		return axios.get(`/common/profile`, defaultRequestConfig(token));
	},
	putUserProfile (token, profile) {
		return axios.put(`/common/profile`, profile.toJSONapi(), defaultRequestConfig(token));
	},

	getUserSettings (token) {
		return axios.get(`/common/settings`, defaultRequestConfig(token));
	},
	getUserSetting (token, setting) {
		return axios.get(`/common/settings?key=${setting}`, defaultRequestConfig(token));
	},
	putUserSetting(token, setting, value) {
		return axios.put(`/common/settings/${setting}`, {"data":{"attributes":{"value":value}}}, defaultRequestConfig(token));
	},

	getFolders(token) {
		return axios.get(`/common/folders`, defaultRequestConfig(token));
	},
	saveFolder(token, folder) {
		return (folder.id == -1) ?
			axios.post(`/common/folders`, folder.toJSONapi(), defaultRequestConfig(token)):
			axios.put(`/common/folders/${folder.id}`, folder.toJSONapi(), defaultRequestConfig(token));
	},
	deleteFolder(token, folderId) {
		return axios.delete(`/common/folders/${folderId}`, defaultRequestConfig(token));
	},
	getMessages(token, folderid, page=1, per_page=25) {
		return axios.get(`/common/folders/${folderid}/messages?page=${page}&per_page=${per_page}`, defaultRequestConfig(token));
	},
	getMessage(token, folderId, msgId) {
		return axios.get(`/common/folders/${folderId}/messages/${msgId}`, defaultRequestConfig(token));
	},
	deleteMessage(token, folderId, msgId) {
		return axios.delete(`/common/folders/${folderId}/messages/${msgId}`, defaultRequestConfig(token));
	},
	restoreMessage(token, folderid, messageid) {
		return axios.put(`/common/folders/${folderid}/messages/${messageid}/restore`, {}, defaultRequestConfig(token));
	},
	postDraft(token, message) {
		return axios.post(`/common/draft`, message.toJSONapi(), defaultRequestConfig(token));
	},
	putDraft(token, message) {
		return axios.put(`/common/folders/${message.folder_id}/messages/${message.id}`, message.toJSONapi(), defaultRequestConfig(token) );
	},
	postMessage(token, msg) {
		return axios.post(`/common/message`, msg.toJSONapi(), defaultRequestConfig(token));
	},
	putMoveMessages(token, folderId, destFolderId, messages) {
		return axios.put(`/common/folders/${folderId}/messages/move_to/${destFolderId}`, {"data":{"attributes":{"message_ids": messages}}}, defaultRequestConfig(token) );
	},


	getBroadcasts(token) {
		return axios.get(`/common/broadcasts`, defaultRequestConfig(token));
	},
  getPublicBroadcasts(){
    return axios.get('/public/broadcasts', defaultRequestConfig(""));
  },
	deleteBroadcast(token, id) {
		return axios.delete(`/common/broadcasts/${id}`, defaultRequestConfig(token));
	},
	putBroadcast(token, id) {
		return axios.put(`/common/broadcasts/${id}`, {}, defaultRequestConfig(token));
	},


	getUsers(token) {
		return axios.get(`/common/recipients`, defaultRequestConfig(token));
	},

	getPreScreens(token) {
		return axios.get(`/client/prescreen`, defaultRequestConfig(token));
	},
	postPreScreenEnrol(token, program_id) {
		return axios.post(`/client/enrollments`, {"data": {"attributes": {"program_id": program_id}}}, defaultRequestConfig(token));
	},

	getEnrollments(token) {
		return axios.get(`/client/enrollments?timeframe=all`, defaultRequestConfig(token));
	},
	getEnrollment(token, enrol_id) {
		return axios.get(`/client/enrollments/${enrol_id}`, defaultRequestConfig(token));
	},

	getResources (token, enrol_id) {
		return axios.get(`/client/enrollments/${enrol_id}/resources`, defaultRequestConfig(token));
	},

	getFlaggedResources (token, enrol_id) {
		return axios.get(`/client/enrollments/${enrol_id}/resources/flagged`, defaultRequestConfig(token));
	},

	getSurveys (token, enrol_id, completed=false) {
		let flag = (completed) ? 'completed=true' : '';
		return axios.get(`client/enrollments/${enrol_id}/surveys?${flag}`, defaultRequestConfig(token));
	},

	getStages (token, enrol_id) {
		return axios.get(`client/enrollments/${enrol_id}/stages`, defaultRequestConfig(token));
	},
	getStage (token, enrol_id, stage_id) {
		return axios.get(`client/enrollments/${enrol_id}/stages/${stage_id}`, defaultRequestConfig(token));
	},

	getContent (token, enrol_id, stage_id, task_id, page_id) {
		return axios.get(`client/enrollments/${enrol_id}/stages/${stage_id}/tasks/${task_id}/pages/${page_id}`, defaultRequestConfig(token));
	},

	putPageView (token, enrol_id, stage_id, task_id, page_id) {
		return axios.put(`client/enrollments/${enrol_id}/stages/${stage_id}/tasks/${task_id}/pages/viewed/${page_id}`, {}, defaultRequestConfig(token));
	},

	fetchSurveyResult (token, enrol_id, surveylink_id=null, task_id=null) {
		return axios.get(`/client/enrollments/${enrol_id}/` + ((surveylink_id)?`surveys/${surveylink_id}`:`tasks/${task_id}`), defaultRequestConfig(token));
	},

	updateSurveyResultEntry (token, enrol_id, surveylink_id=null, task_id=null, result_id, data) {
		//let id = (result_entry.id > 0) ? result_entry.id : 0;
		return axios.post(`/client/enrollments/${enrol_id}/` + ((surveylink_id) ? `surveys/${surveylink_id}/` : `tasks/${task_id}/`) + `results/${result_id}/result_entries`, data, defaultRequestConfig(token));
	},

	completeSurveyResult (token, enrol_id, surveylink_id, task_id, result_id, data={}) {
		return axios.post(`/client/enrollments/${enrol_id}/`+ ((surveylink_id) ? `surveys/${surveylink_id}/` : `tasks/${task_id}/`) + `/results/${result_id}/complete`, data, defaultRequestConfig(token));
	},

	postEnrollmentView (token, enrol_id) {
		return axios.post(`/client/enrollments/${enrol_id}/visit`, {}, defaultRequestConfig(token));
	},

	postWithdraw (token, enrol_id, note) {
		return axios.post(`/client/enrollments/${enrol_id}/withdraw`, {"data": {"attributes": {"termination_note": note}}}, defaultRequestConfig(token));
	},

	getGraphs (token, enrol_id) {
		return axios.get(`/client/enrollments/${enrol_id}/graphs`, defaultRequestConfig(token));
	},

	putAdditionalResourceView (token, enrol_id, resource_id) {
		return axios.put(`client/enrollments/${enrol_id}/resources/viewed/${resource_id}`, {}, defaultRequestConfig(token));
	},

	putPageResourceView (token, enrol_id, stage_id, task_id, page_id, resource_id) {
		return axios.put(`client/enrollments/${enrol_id}/stages/${stage_id}/tasks/${task_id}/pages/${page_id}/resources/viewed/${resource_id}`, {}, defaultRequestConfig(token));
	},
	getSharedFiles (token, enrollment_id) {
		return axios.get(`client/enrollments/${enrollment_id}/shared_files`, defaultRequestConfig(token));
	},
	postSharedFiles (token, formData, enrollment_id) {
		return axios.post(`client/enrollments/${enrollment_id}/shared_files`, formData, {
			'baseURL': process.env.VUE_APP_API_URL,
			'headers': {
				'Content-Type': 'multipart/form-data',
				'Authorization': `Bearer ${token}`,
				'Accept': '*/*'
			}
		});
	},
	hideSharedFile (token, shared_file_id, enrollment_id) {
		return axios.put(`client/enrollments/${enrollment_id}/shared_files/${shared_file_id}`, {"data":{"attributes":{"value":"hide"}}}, defaultRequestConfig(token));
	},
	
	fetchForums(token, page, per_page) {
		return axios.get(`/common/forums?page=${page}&per_page=${per_page}&pagination=true`, defaultRequestConfig(token));
    },
	fetchTopics (token, forum_id, section_id, page, per_page) {
		return axios.get(`/common/forums/${forum_id}/sections/${section_id}/topics?page=${page}&per_page=${per_page}`, defaultRequestConfig(token));
	},
	createTopic (token, forum_id, section_id, topic) {
		return axios.post(`/common/forums/${forum_id}/sections/${section_id}/topics`, topic.toJSONapi(), defaultRequestConfig(token));
	},
	fetchPosts (token, forum_id, topic_id, page, per_page) {
		return axios.get(`/common/forums/${forum_id}/topics/${topic_id}/posts?page=${page}&per_page=${per_page}`, defaultRequestConfig(token));
	},
	createPost (token, forum_id, topic_id, post) {
		return axios.post(`/common/forums/${forum_id}/topics/${topic_id}/posts`, post.toJSONapi(), defaultRequestConfig(token));
	},
	getSection (token,forum_id, section_id) {
		return axios.get(`/staff/forums/${forum_id}/sections/${section_id}`, defaultRequestConfig(token));
	},
	getSectionPostCount(token, forum_id){
		return axios.get(`/common/forums/${forum_id}/section_post_count`, defaultRequestConfig(token));
	},
	createModerationAction (token, forum_id, post_id, moderation_action) {
		return axios.post(`/common/forums/${forum_id}/posts/${post_id}/report`, moderation_action.toJSONapi(), defaultRequestConfig(token));
	},
	saveConsentDate (token, id) {
		return axios.post(`/client/enrollments/${id}/consent`, {"data":{"attributes":{}}}, defaultRequestConfig(token));
	},
	getFurthestPageView (token, client_id, enrol_id) {
		return axios.get(`/client/${client_id}/enrollments/${enrol_id}/furthest_visited_page`, defaultRequestConfig(token));
	}, 

	getProvinces() {
		return axios.get(`/public/provinces`, defaultRequestConfig(""));
	},
	getUserSources() {
		return axios.get(`/public/client_sources`, defaultRequestConfig(""));
	},
	fetchAdditionalResourceCategories  (token) {
		return axios.get(`/staff/additional_resource_categories`, defaultRequestConfig(token));
	},
	getcourseInterests(){
		return axios.get(`/public/course_interests`, defaultRequestConfig(""));
	},
	getVideos (token, vtype, clientId) {
		return axios.get(`/client/videos?vtype=${vtype}`, defaultRequestConfig(token));
	},


}
